import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import { connect } from 'react-redux';
import * as FormActions from '../../actions/actionCreators';
import {
  type FieldsetValidator,
  initFieldsetValidator,
} from '../../utils/FieldsetValidator';
import Button from './Button';
import type { RootState } from '../../store';
import { bindActionCreators } from '@reduxjs/toolkit';

interface FormProps {
  name: string;
  formActions?: {
    nextForm: (name: string, index: number) => void;
    backForm: (name: string, index: number) => void;
    submitContactForm: (formAction: string) => void;
    submitForm: (name: string, index: number) => void;
  };
  children?: React.ReactNode;
  formAction?: string;
  index?: number;
  formType?: 'complaint' | 'contact' | 'report';
  lastStep?: boolean;
}

// Create context for fieldset validator
export const FieldsetValidatorContext = createContext<FieldsetValidator>(
  initFieldsetValidator()
);

const Form: React.FC<FormProps> = ({
  name,
  formActions,
  children,
  formAction,
  index = 0,
  formType,
  lastStep,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const parentContainerRef = useRef<HTMLElement | null>(null);
  const headerElmRef = useRef<HTMLElement | null>(null);
  const fieldsetValidator = useContext(FieldsetValidatorContext);

  useEffect(() => {
    parentContainerRef.current = document.getElementById('form-stepper-wrap');
    headerElmRef.current = document.querySelector('.header--mobile');
  }, []);

  const scrollToContainer = () => {
    if (parentContainerRef.current) {
      parentContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });

      if (headerElmRef.current) {
        setTimeout(() => {
          headerElmRef.current?.classList.add('header__container--hidden');
        }, 500);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (fieldsetValidator.isValid(true)) {
      if (!isLoading) {
        setIsLoading(true);
      }

      if (!isComplete) {
        setIsComplete(true);
      }

      if (formType === 'complaint') {
        formActions.nextForm(name, index);
        scrollToContainer();
      } else if (formType === 'contact') {
        formActions.submitContactForm(formAction || '');
      } else {
        formActions.submitForm(name, index);
      }
    }
  };

  const handleBackSubmit = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!isLoading) {
      setIsLoading(true);
    }

    // only allow 'back' if form has been completed and is valid
    formActions.backForm(name, index);
    scrollToContainer();
  };

  const renderButton = () => {
    const buttonLabel = formType === 'contact' ? 'submit' : 'Next';

    if (formType === 'contact' && isLoading) {
      return <div className="loader" />;
    }

    return (
      <Button
        className="step__button button button--primary button--align-right"
        data-id={index}
        onClick={(e) => {
          if (fieldsetValidator.isValid(true)) {
            handleSubmit(e);
          }
        }}
      >
        {buttonLabel}
      </Button>
    );
  };

  const renderBackButton = () => {
    if (index !== 0) {
      return (
        <Button
          onClick={handleBackSubmit}
          className="step__button button button--tertiary button--align-left"
          data-id={index}
        >
          Back
        </Button>
      );
    }
    return null;
  };

  return (
    <form noValidate onSubmit={handleSubmit} action={formAction}>
      {React.Children.map(children, (field) => {
        if (!field) return null;

        return React.cloneElement(field as React.ReactElement, {
          ...(field as React.ReactElement).props,
          form: name,
        });
      })}

      {formType === 'complaint'
        ? !lastStep && (
            <div className="form-actions">
              {renderBackButton()}
              {renderButton()}
            </div>
          )
        : renderButton()}
    </form>
  );
};

const mapStateToProps = (state: RootState, props: FormProps) => ({
  isComplete: state.forms[props.name].isComplete,
});

const mapDispatchToProps = (dispatch: any) => ({
  formActions: bindActionCreators(FormActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form);
